import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import css from './ServiceRequestsTable.module.scss';
import { RouteComponentProps } from '@reach/router';
import { Column, PaginationTable } from '../PaginationTable/PaginationTable';
import { compact } from 'lodash';
import { cns, isLoading, logger } from '../../utils';
import { useOvermindState } from '../../state';
import Search from '../../assets/search.svg';
import { Input } from '../Input/Input';
import queryString from 'query-string';
import { Checkbox, ClickAwayListener } from '@mui/material';
import { Button } from '../Button/Button';
import { api } from '../../state/effects';
import { useAsyncData } from '../../hooks';
import { Dropdown } from '../DropDown/DropDown';
import { Email, EmailType, EmailStatus, MaintenanceType, ServiceRequest } from '../../../interfaces/customer-api';
import { Spinner } from '../Spinner/Spinner';

const filterEquipment = (email: Email, filterValue: string): boolean => {
    const searchValues = [
        // email.customerNumber,
        // email.customerName,
        email.userName,
        email.createdOn,
        email.emailBodyJson ? (email.emailBodyJson as ServiceRequest).serialNumber : '',
        // email.emailBodyJson ? (email.emailBodyJson as ServiceRequest).description : '',
        email.emailBodyJson ? (email.emailBodyJson as ServiceRequest).maintenanceType : '',
    ].map((value) => (value ? value.toLowerCase() : ''));

    return searchValues.some((value) => value.includes(filterValue.toLowerCase()));
};

const CompleteRequestDialog: React.FC<{
    customerNumber: string | null;
    selectedEmailId: string | null;
    email: Email;
    setIsCompleteDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setSelectedEmailId: React.Dispatch<React.SetStateAction<string | null>>;
}> = ({ setIsCompleteDialogOpen, setSelectedEmailId, customerNumber, selectedEmailId, email }) => {
    const { t } = useTranslation('ServiceRequests');

    return (
        <div className={css.darkBackground}>
            <ClickAwayListener
                onClickAway={() => {
                    setIsCompleteDialogOpen(false);
                    setSelectedEmailId(null);
                }}
            >
                <div className={css.completeDialog}>
                    <div className={css.dialogHeader}>{t('Mark request as complete')}</div>
                    <div className={css.dialogText}>{t("This can't be changed")}</div>
                    <div className={css.dialogButtonContainer}>
                        <Button
                            variant="primary"
                            onClick={() => {
                                setIsCompleteDialogOpen(false);
                                setSelectedEmailId(null);
                            }}
                        >
                            {t('Cancel')}
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => {
                                {
                                    if (customerNumber && selectedEmailId) {
                                        email.status = EmailStatus.Archived;
                                        api.updateEmail(customerNumber, selectedEmailId, email);
                                    }
                                }
                                setIsCompleteDialogOpen(false);
                                setSelectedEmailId(null);
                            }}
                        >
                            {t('OK')}
                        </Button>
                    </div>
                </div>
            </ClickAwayListener>
        </div>
    );
};

enum Filters {
    Default = 'All',
    Open = 'Open',
    Closed = 'Closed',
}

export const ServiceRequestsTable: React.FC<RouteComponentProps> = () => {
    const { t } = useTranslation('ServiceRequests');
    const [filterValue, setFilterValue] = useState('');
    const [isCompleteDialogOpen, setIsCompleteDialogOpen] = useState(false);
    const [selectedEmail, setSelectedEmailId] = useState<string | null>(null);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const { selectedCustomerNumber, loadingStates } = useOvermindState();
    const [selectedFilter, setSelectedFilter] = useState<string>(Filters.Default);

    const loadEmails = () => {
        if (!selectedCustomerNumber) return Promise.resolve([]);
        return api.getEmails(selectedCustomerNumber, EmailType.RequestMaintenance);
    };
    const [emails, isEmailsLoading] = useAsyncData(loadEmails, [], [selectedCustomerNumber]);

    // TODO: Check if this is needed. Not needed if no links with default search values
    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            // Check if url contains a search term on first render
            const query = queryString.parse(window.location.search);
            if (query && query.search) {
                setFilterValue(query.search as string);
            }
            isFirstRender.current = false;
        } else {
            const query = queryString.stringify({ search: filterValue });
            window.history.replaceState(null, '', `?${query}`);
        }
    }, [filterValue]);

    const columns: Column<Email>[] = compact([
        {
            title: t('Customer number'),
            accessor: (row: Email) => row.customerNumber,
            className: css.customerNumber,
        },
        {
            title: t('Customer name'),
            accessor: (row: Email) => row.customerName || t('Unknown'),
            className: css.customerName,
        },
        {
            title: t('User'),
            accessor: (row: Email) => row.userName || t('Unknown'),
            className: css.user,
        },
        {
            title: t('Maintenance type'),
            accessor: (row: Email) => {
                try {
                    const serviceRequest = row.emailBodyJson as ServiceRequest;
                    if (
                        serviceRequest &&
                        serviceRequest.maintenanceType !== undefined &&
                        serviceRequest.maintenanceType !== null
                    ) {
                        return serviceRequest.maintenanceType;
                    } else {
                        logger.error('maintenanceType is missing or null/undefined for row:', row);
                        return MaintenanceType.Other;
                    }
                } catch (error) {
                    logger.error('Error accessing maintenance type:', error);
                    return MaintenanceType.Other;
                }
            },
            className: css.maintenanceType,
        },
        {
            title: t('Serial number'),
            accessor: (row: Email) => {
                try {
                    const serviceRequest = row.emailBodyJson as ServiceRequest;
                    if (
                        serviceRequest &&
                        serviceRequest.serialNumber !== undefined &&
                        serviceRequest.serialNumber !== null
                    ) {
                        return serviceRequest.serialNumber;
                    } else {
                        logger.error('serialNumber is missing or null/undefined for row:', row);
                        return t('Unknown');
                    }
                } catch (error) {
                    logger.error('Error accessing serial number:', error);
                    return t('Unknown');
                }
            },
            className: css.serialNumber,
        },
        {
            title: t('Description'),
            accessor: (row: Email) => {
                try {
                    const description = row.emailBodyJson ? (row.emailBodyJson as ServiceRequest).description : '';
                    return description.length > 30 ? description.slice(0, 30) + '...' : description;
                } catch (error) {
                    logger.error('Error accessing description:', error);
                    return t('Unknown');
                }
            },
            className: css.description,
        },
        {
            title: t('Created'),
            accessor: (row: Email) => {
                try {
                    const date = row.createdOn ? new Date(row.createdOn) : null;
                    return date ? date.toLocaleDateString() : t('Unknown');
                } catch (error) {
                    logger.error('Error accessing createdOn:', error);
                    return t('Unknown');
                }
            },
            className: css.created,
        },
        {
            title: t('Completed'),
            accessor: (row: Email) => (
                <Checkbox
                    checked={row.status === EmailStatus.Archived}
                    sx={{
                        color: '#2c112d',
                        '& .MuiSvgIcon-root': { fontSize: 22 },
                    }}
                    onChange={() => {
                        if (row.id) {
                            setSelectedEmailId(row.id);
                            setIsCompleteDialogOpen(true);
                        } else {
                            logger.log('Email ID is not available for this row');
                        }
                    }}
                />
            ),
            className: cns(css.centered),
        },
    ]);

    const handleChangeRowsPerPage = (value: number) => {
        setRowsPerPage(value);
    };

    const getRequestsToShow = () => {
        switch (selectedFilter) {
            case Filters.Open:
                return emails.filter(
                    (row: Email) =>
                        (filterValue ? filterEquipment(row, filterValue) : true) && row.status !== EmailStatus.Archived,
                );
            case Filters.Closed:
                return emails.filter(
                    (row: Email) =>
                        (filterValue ? filterEquipment(row, filterValue) : true) && row.status === EmailStatus.Archived,
                );
            default:
                return emails.filter((row: Email) => (filterValue ? filterEquipment(row, filterValue) : true));
        }
    };

    return (
        <div className={css.container}>
            <div className={css.controlsContainer}>
                <Input
                    containerProps={{
                        className: css.searchField,
                    }}
                    icon={<Search />}
                    id="my-equipment-search"
                    inputProps={{
                        onChange: ({ target }) => setFilterValue(target.value),
                        placeholder: `${t('Search request')}`,
                        type: 'text',
                        value: filterValue,
                        className: css.searchFieldInput,
                    }}
                />
                <Button
                    id="create-new-service-request-button"
                    variant="primary"
                    linkTo="./form"
                    className={css.createButton}
                >
                    + {t('Create new')}
                </Button>
            </div>
            {isEmailsLoading ? (
                <div className={css.centeredText}>
                    <Spinner size={3} />
                </div>
            ) : emails.length === 0 ? (
                <div className={css.centeredText}>{t('No service requests')}</div>
            ) : (
                <>
                    <div className={css.filterContainer}>
                        <Dropdown
                            handleChange={(filter: string) => {
                                setSelectedFilter(filter);
                            }}
                            label={`${t('Show requests')}:`}
                            options={Object.values(Filters).map((filter: string) => ({
                                id: filter,
                                label: t(filter),
                            }))}
                            selected={selectedFilter}
                            className={css.requestFilter}
                        />
                        <Dropdown
                            handleChange={handleChangeRowsPerPage}
                            label={`${t('Show on page')}:`}
                            options={[
                                { id: 20, label: '20' },
                                { id: 40, label: '40' },
                                { id: 60, label: '60' },
                            ]}
                            selected={rowsPerPage}
                            className={css.paginationFilter}
                        />
                    </div>
                    <PaginationTable
                        columns={columns}
                        data={getRequestsToShow()}
                        className={css.table}
                        id="service-requests-table"
                        isLoading={isLoading(loadingStates.serviceRequests)}
                        rowsPerPage={rowsPerPage}
                    />
                    {isCompleteDialogOpen && (
                        <CompleteRequestDialog
                            customerNumber={selectedCustomerNumber}
                            selectedEmailId={selectedEmail}
                            email={emails.find((email) => email.id === selectedEmail) || ({} as Email)}
                            setIsCompleteDialogOpen={setIsCompleteDialogOpen}
                            setSelectedEmailId={setSelectedEmailId}
                        />
                    )}
                </>
            )}
        </div>
    );
};
